import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { environment } from '@env';
import { Observable, switchMap, throwError, from } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isPublicRoute } from 'pi-api';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private inj = inject(Injector);

// Resolved lazily (not in the constructor) to avoid a circular dependency: AuthService -> HttpClient -> this interceptor.
  private get authService(): AuthService {
    return this.inj.get(AuthService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(catchError((error: HttpErrorResponse) => this.errorHandler(error, request, next)));
  }

  private errorHandler(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.logError(error);
    if (error.status === 401) {
      return this.handleUnauthorizedResponse(error, request, next);
    }
    return throwError(() => error);
  }

  private logError(error: HttpErrorResponse): void {
    if (!environment.production) {
      const message = this.getMessageFromResponse(error);
      console.error(message ?? error.statusText);
    }
  }

  private getMessageFromResponse(error: HttpErrorResponse): string | null {
    if (!(error.error instanceof ArrayBuffer)) {
      return error?.error?.message || error?.message || '';
    }

    const errorArray = String.fromCharCode.apply(null, new Uint8Array(error.error) as any);
    return JSON.parse(errorArray).message;
  }

  private handleUnauthorizedResponse(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    console.debug('ErrorHandler: 401 received for', request.url);

    // Jeśli to już retry request - nie próbuj ponownie (zapobiegaj infinite loop)
    if (request.headers.has('X-Retry-Request')) {
      console.error('ErrorHandler: 401 on retry request, giving up', request.url);
      return throwError(() => error);
    }

    // Sprawdź czy to auth endpoint (nie retry auth requests)
    if (this.isAuthEndpoint(request.url)) {
      console.debug('ErrorHandler: 401 on auth endpoint, not retrying', request.url);
      return throwError(() => error);
    }

    // Wywołaj refresh przez AuthService (używa mutex - safe dla concurrent calls)
    console.debug('ErrorHandler: attempting refresh and retry for', request.url);
    return from(this.authService.getRefreshPromise()).pipe(
      switchMap(() => {
        console.debug('ErrorHandler: refresh success, retrying request', request.url);
        // Retry z headerem X-Retry-Request (JwtModule doda nowy token automatycznie)
        const retryRequest = request.clone({
          setHeaders: {
            'X-Retry-Request': 'true',
          },
        });
        return next.handle(retryRequest);
      }),
      catchError((refreshError) => {
        console.error('ErrorHandler: refresh failed, cannot retry', request.url, refreshError);
        // Refresh failed (AuthService already called logOut) - propaguj oryginalny 401 error
        return throwError(() => error);
      })
    );
  }

  /**
   * Trasy bez tokenu - wspolna lista z `pi-api`. 401 z logowania albo z drugiego skladnika
   * ma trafic do formularza, bez odswiezania sesji i przekierowania.
   */
  private isAuthEndpoint(url: string): boolean {
    return isPublicRoute(url);
  }

}
